import { useState, useEffect, useCallback, useRef } from 'react';
import { NavigationHistoryService } from '../services/NavigationHistoryService';
import type { NavigationEvent } from '../services/NavigationHistoryService';
import { useAuth } from '../auth/useAuth';

export const useNavigationHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState<NavigationEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const lastRefreshTime = useRef<number>(0);
  const visitCache = useRef<Set<string>>(new Set());

  // Generate a session ID for this browser session
  const sessionId = useState(() => 
    `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  )[0];

  // Helper to deduplicate events properly
  const deduplicateEvents = (events: NavigationEvent[]): NavigationEvent[] => {
    const seen = new Map<string, NavigationEvent>();
    
    // Sort by timestamp descending first
    const sortedEvents = [...events].sort((a, b) => b.timestamp - a.timestamp);
    
    for (const event of sortedEvents) {
      const key = `${event.type}-${event.id}`;
      if (!seen.has(key) || seen.get(key)!.timestamp < event.timestamp) {
        seen.set(key, event);
      }
    }
    
    return Array.from(seen.values())
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 20);
  };

  const loadHistory = useCallback(async (forceRefresh = false) => {
    if (!user?.username) return;
    
    // Throttle requests - only refresh if more than 5 seconds have passed
    const now = Date.now();
    if (!forceRefresh && now - lastRefreshTime.current < 5000) {
      return;
    }
    
    setLoading(true);
    lastRefreshTime.current = now;
    
    try {
      const events = await NavigationHistoryService.getHistory(user.username);
      const deduplicatedEvents = deduplicateEvents(events);
      
      // Direct state update instead of debounced
      setHistory(deduplicatedEvents);
      
      // Update visit cache
      visitCache.current = new Set(
        deduplicatedEvents.map(e => `${e.type}-${e.id}`)
      );
    } catch (error) {
      console.error('Failed to load navigation history:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.username]); // Remove debouncedSetHistory dependency

  const recordVisit = useCallback(async (
    type: 'machine' | 'line',
    id: string,
    name: string
  ) => {
    if (!user?.username) return;

    // Check if we've already recorded this visit recently (avoid duplicates)
    const visitKey = `${type}-${id}`;
    if (visitCache.current.has(visitKey)) {
      return; // Skip if already recorded in this session
    }

    try {
      // Create new event
      const newEvent: NavigationEvent = {
        type,
        id,
        name,
        timestamp: Date.now(),
        sessionId,
        userId: user.username
      };

      // Add to local state immediately for instant UX using functional update
      setHistory(prevHistory => {
        const updated = [newEvent, ...prevHistory];
        return deduplicateEvents(updated);
      });

      // Add to cache
      visitCache.current.add(visitKey);

      // Record in background (fire and forget)
      NavigationHistoryService.recordVisit(
        type,
        id,
        name,
        user.username,
        sessionId
      ).catch(error => {
        console.error('Failed to record navigation:', error);
        // Remove from cache if recording failed
        visitCache.current.delete(visitKey);
      });
    } catch (error) {
      console.error('Failed to record navigation:', error);
    }
  }, [sessionId, user?.username]); // Remove history and debouncedSetHistory dependencies

  const clearHistory = useCallback(async () => {
    if (!user?.username) return;
    
    try {
      await NavigationHistoryService.clearHistory(user.username);
      setHistory([]);
      visitCache.current.clear();
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  }, []); // Remove user?.username dependency

  // Load history when user changes
  useEffect(() => {
    if (user?.username) {
      loadHistory(true); // Force refresh on user change
    } else {
      setHistory([]);
      visitCache.current.clear();
    }
  }, [user?.username, loadHistory]);

  // Initialize the service
  useEffect(() => {
    NavigationHistoryService.initialize().catch(console.error);
  }, []);

  // Stable refresh function that doesn't change on every render
  const stableRefresh = useCallback(() => {
    if (user?.username) {
      loadHistory(true);
    }
  }, [user?.username, loadHistory]);

  return {
    history,
    loading,
    recordVisit,
    clearHistory,
    refresh: stableRefresh
  };
};